import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
/** Keep the visible hotel (or focused control) at its viewport offset as cards arrive. */
export function useHotelScrollAnchor(root: RefObject<HTMLElement>, revision: unknown, scope?: string) {
  const anchor = useRef<{ element: HTMLElement; top: number } | null>(null);
  const remember = () => {
    const container = root.current;
    if (!container) return;
    const focus = document.activeElement;
    const element =
      focus instanceof HTMLElement && container.contains(focus)
        ? focus
        : [...container.querySelectorAll<HTMLElement>('[data-hotel-id], [data-hotel-fare]')].find(
            (node) => node.getBoundingClientRect().bottom > 0 && node.getBoundingClientRect().top < window.innerHeight,
          );
    anchor.current = element ? { element, top: element.getBoundingClientRect().top } : null;
  };
  useEffect(() => {
    anchor.current = null;
    window.addEventListener('scroll', remember, true);
    window.addEventListener('focusin', remember);
    return () => {
      window.removeEventListener('scroll', remember, true);
      window.removeEventListener('focusin', remember);
    };
  }, [scope]);
  useLayoutEffect(() => {
    const previous = anchor.current;
    if (previous?.element.isConnected) {
      const difference = previous.element.getBoundingClientRect().top - previous.top;
      if (difference) {
        let scroll = previous.element.parentElement;
        while (
          scroll &&
          (!/(auto|scroll)/.test(getComputedStyle(scroll).overflowY) || scroll.scrollHeight <= scroll.clientHeight)
        )
          scroll = scroll.parentElement;
        if (scroll) scroll.scrollTop += difference;
        else window.scrollBy(0, difference);
      }
    }
    remember();
  }, [revision]);
}
